/**
 * Sales chatbot conversation persistence service.
 * Calls the main backend at /api/v1/sales-chat/...
 * Every function returns null on any error so callers can fall back to localStorage.
 */
import axios from "axios";
import type { ConversationSummary } from "../types/multiChatbot";

const BASE =
  (process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1").replace(/\/$/, "");

export interface SalesStoredMessage {
  id?: string;
  role: "user" | "assistant";
  text: string;
  api_history_content?: string | null;
  is_error?: boolean;
  created_at?: string;
}

export interface SalesConversation {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  messages: SalesStoredMessage[];
}

function authHeaders(): Record<string, string> {
  const token =
    typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function listConversations(): Promise<ConversationSummary[] | null> {
  try {
    const res = await axios.get<ConversationSummary[]>(`${BASE}/sales-chat/conversations`, {
      headers: authHeaders(),
    });
    return res.data;
  } catch {
    return null;
  }
}

export async function createConversation(
  title = "New conversation"
): Promise<SalesConversation | null> {
  try {
    const res = await axios.post<SalesConversation>(
      `${BASE}/sales-chat/conversations`,
      { title },
      { headers: authHeaders() }
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function getConversation(id: string): Promise<SalesConversation | null> {
  try {
    const res = await axios.get<SalesConversation>(`${BASE}/sales-chat/conversations/${id}`, {
      headers: authHeaders(),
    });
    return res.data;
  } catch {
    return null;
  }
}

export async function deleteConversation(id: string): Promise<boolean> {
  try {
    await axios.delete(`${BASE}/sales-chat/conversations/${id}`, { headers: authHeaders() });
    return true;
  } catch {
    return false;
  }
}

export async function renameConversation(
  id: string,
  title: string
): Promise<SalesConversation | null> {
  try {
    const res = await axios.patch<SalesConversation>(
      `${BASE}/sales-chat/conversations/${id}`,
      { title },
      { headers: authHeaders() }
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function appendMessages(
  conversationId: string,
  messages: SalesStoredMessage[]
): Promise<boolean> {
  try {
    await axios.post(
      `${BASE}/sales-chat/conversations/${conversationId}/messages`,
      { messages },
      { headers: authHeaders() }
    );
    return true;
  } catch {
    return false;
  }
}
